import React from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { Crown, Sparkles, Sword, GripVertical, X, Trash2 } from 'lucide-react';
import Card from '../../components/ui/Card';
import {BUFF_CLASSES, BUFFS_MINIMO, getClassCollor, getClassTypeCollor} from "../../utils/characterTranslation";

const PartyCard = ({ party, index, onRemoveParty, onRemoveMember, participants = [], eventName = '', allParties = [] }) => {
  // Conta em quantos grupos um buff compartilhado está
  const countBuffInParties = (participantId) => {
    if (!allParties || allParties.length === 0) return 0;
    
    let count = 0;
    allParties.forEach(p => {
      if (p.members && p.members.some(m => m.id === participantId)) {
        count++;
      }
    });
    
    return count;
  };

  // Retorna estilo baseado no número de grupos
  const getBuffAlertBadge = (count) => {
    if (count <= 3) {
      return { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30', icon: '✅' };
    }
    if (count === 4) {
      return { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30', icon: '⚠️' };
    }
    return { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', icon: '🔴', pulse: true };
  };

  const isBuffClass = (charClass) => {
    return BUFF_CLASSES.includes(charClass?.toUpperCase());
  };

  const isSharedBuff = (memberId) => {
    const participant = participants.find(p => p.id === memberId);
    return participant?.isBuff || false;
  };

  // Separa membros regulares de buffs compartilhados
  const regularMembers = party.members?.filter(m => !isSharedBuff(m.id)) || [];
  const sharedBuffs =
    party.members
      ?.filter(m => isSharedBuff(m.id))
      .sort((a, b) => {
        const classA = a.character?.class?.toUpperCase() || '';
        const classB = b.character?.class?.toUpperCase() || '';

        return classA.localeCompare(classB);
      }) || [];

  const getMissingBuffs = () => {
    const classesNoGrupo = party.members
      ?.map(m => m.character?.class?.toUpperCase())
      .filter(Boolean) || [];

    return BUFFS_MINIMO.filter(
      buffClass => !classesNoGrupo.includes(buffClass)
    );
  };

  const getClassIcon = (member, index) => {
    // Primeiro da lista de regulares é sempre o líder
    if (index === 0 && !isSharedBuff(member.id)) {
      return <Crown className="w-4 h-4 text-yellow-400" title="Líder do Grupo" />;
    }
    if (isSharedBuff(member.id)) {
      return <Sparkles className={getClassCollor(member.character?.class) + ' w-4 h-4'} title="Buff Compartilhado - pode estar em vários grupos" />;
    }
    if (isBuffClass(member.character?.class)) {
      return <Sparkles className={getClassCollor(member.character?.class) + ' w-4 h-4'} />;
    }
    return <Sword className="w-4 h-4 text-gray-400" />;
  };

  const relics = (member) => {
    const relic = [];

    if (member.character?.hasYag) relic.push('Yag');
    if (member.character?.hasBabel) relic.push('Babel');
    if (member.character?.hasDraxos) relic.push('Draxos');
    if (member.character?.hasMidranda) relic.push('Midranda');

    return relic;
  }

  const renderMember = (member, idx, isRegular = true) => {
    const participant = participants.find(p => p.id === member.id);
    const hasPilot = participant?.pilot;

    return (
    <Draggable
      key={`${party.id}-${member.id}-${idx}`}
      draggableId={`${party.id}-participant-${member.id}-${idx}`}
      index={idx}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors ${
            snapshot.isDragging ? 'shadow-xl shadow-orange-500/50 ring-2 ring-orange-500' : ''
          }`}
        >
          <GripVertical className="w-4 h-4 text-gray-500 flex-shrink-0" />

          <div className="flex-shrink-0">
            {getClassIcon(member, idx)}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-white font-medium truncate">
                {isRegular ? String(idx + 1).padStart(2, '0') + '.' : ''} {member.character?.name || 'N/A'}
              </p>
              <span className="text-gray-500">→</span>
              <span className="text-gray-400 text-sm">{member.character?.class}</span>
              {idx === 0 && isRegular && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 font-semibold">
                  Líder da PT
                </span>
              )}
            </div>
            <p className="text-gray-500 text-xs mt-0.5">
              Nível {member.character?.level}
              {hasPilot && (
                <span className="text-orange-400"> • Piloto: {participant.pilot}</span>
              )}
            </p>
            {relics(member).length > 0 && (
              <p className="text-gray-500 font-extrabold text-xs mt-0.5">
                {relics(member).join(' • ')}
              </p>
            )}
          </div>

          {onRemoveMember && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemoveMember(party.id, member.id);
              }}
              className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 hover:text-red-400 border border-white/10 transition-all"
              title="Remover do grupo"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      )}
    </Draggable>
  );
  };

  return (
    <Card variant="glass" className="hover:border-orange-500/50 transition-colors">
      <div className="space-y-4">
        {/* Header do grupo */}
        <div className="flex justify-between items-center pb-3 border-b border-white/10">
          <div className="flex-1">
            <h3 className="text-lg font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
              Grupo {String(party.partyNumber || party.id).padStart(2, '0')}:
            </h3>
            <div className="flex flex-wrap gap-3 text-sm text-gray-400 mt-1">
              <span className={regularMembers.length > 6 ? 'text-red-400 font-semibold' : ''}>
                {regularMembers.length}/6 regulares
              </span>
              {getMissingBuffs().length > 0 && (
                <>
                  <span>•</span>
                  <span className="text-purple-400">Buffs que faltam: {getMissingBuffs().join(', ')}</span>
                </>
              )}
            </div>
          </div>
          {onRemoveParty && (
            <button
              onClick={() => onRemoveParty(party.id)}
              className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 hover:text-red-400 border border-white/10 transition-all"
              title="Remover grupo"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Lista de membros regulares - Droppable */}
        <Droppable droppableId={`party-${party.id}`} type="PARTICIPANT">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`space-y-2 min-h-[100px] rounded-lg p-2 transition-colors ${
                snapshot.isDraggingOver ? 'bg-orange-500/10 border-2 border-dashed border-orange-500' : ''
              }`}
            >
              {regularMembers.length > 0 ? (
                regularMembers.map((member, idx) => renderMember(member, idx, true))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Arraste participantes para cá
                </div>
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>

        {/* Buffs compartilhados */}
        {sharedBuffs.length > 0 && (
          <div className="border-t border-white/10 pt-4">
            <h4 className="text-sm font-semibold text-white mb-2 px-2">Buffs Compartilhados:</h4>
            <div className="space-y-2">
              {sharedBuffs.map((buff, idx) => {
                const participant = participants.find(p => p.id === buff.id);
                const partyCount = countBuffInParties(buff.id);
                const alertBadge = getBuffAlertBadge(partyCount);
                
                return (
                  <div
                    key={`buff-${buff.id}-${idx}`}
                    className={'flex items-center gap-3 p-3 rounded-lg ' + getClassTypeCollor(buff.character?.class)}
                  >
                    <Sparkles className={'w-4 h-4' +  getClassCollor(buff.character?.class)} />
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-medium">
                          Buff {buff.character?.class?.charAt(0).toUpperCase() + buff.character?.class?.slice(1).toLowerCase()}:
                        </span>
                        <span>
                          {buff.character?.name} {participant?.pilot && (
                            <b className="text-gray-400">
                              ({participant.pilot})
                            </b>
                          )}
                        </span>
                      </div>
                      {partyCount > 0 && (
                        <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold ${alertBadge.bg} ${alertBadge.text} border ${alertBadge.border} ${alertBadge.pulse ? 'animate-pulse' : ''}`}>
                          <span>{alertBadge.icon}</span>
                          <span>Em {partyCount} grupo{partyCount !== 1 ? 's' : ''}</span>
                          {partyCount === 4 && <span className="hidden sm:inline">• Atenção</span>}
                          {partyCount >= 5 && <span className="hidden sm:inline">• PERIGO!</span>}
                        </div>
                      )}
                    </div>
                    {onRemoveMember && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveMember(party.id, buff.id);
                        }}
                        className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 hover:text-red-400 border border-white/10 transition-all"
                        title="Remover buff"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default PartyCard;
